/**
 * Owner: Masa Bayu
 * File: author-profile-parts.tsx
 * Date:Time: 2026-10-17 15:02
 * Description: Shared primitives for AuthorProfileSurface — File Law split.
 */

import type { CSSProperties, ReactNode } from 'react';
import { CONTINUE_READING, PROFILE_PERSONA, READING_STAT_TILES } from './author-profile-data';

export function Stroke({
  children,
  size = 18,
}: {
  readonly children: ReactNode;
  readonly size?: number;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function IconChip({
  icon,
  tint,
  ink,
  size = 18,
}: {
  readonly icon: string;
  readonly tint: string;
  readonly ink: string;
  readonly size?: number;
}) {
  return (
    <span className="axp__chip" style={{ '--chip-tint': tint, '--chip-ink': ink } as CSSProperties}>
      <Stroke size={size}>
        <path d={icon} />
      </Stroke>
    </span>
  );
}

export function Card({
  title,
  sub,
  action,
  onAction,
  children,
}: {
  readonly title: string;
  readonly sub?: string;
  readonly action?: string;
  readonly onAction?: () => void;
  readonly children: ReactNode;
}) {
  return (
    <section className="axp__card">
      <div className="axp__card-head">
        <div>
          <h3 className="axp__card-title">{title}</h3>
          {sub !== undefined ? <p className="axp__card-sub">{sub}</p> : null}
        </div>
        {action !== undefined ? (
          onAction !== undefined ? (
            <button type="button" className="axp__card-action" onClick={onAction}>
              {action} →
            </button>
          ) : (
            <span className="axp__card-action">{action} →</span>
          )
        ) : null}
      </div>
      {children}
    </section>
  );
}

export function ProfileReaderRow({ onNavigate }: { readonly onNavigate: (path: string) => void }) {
  return (
    <div className="axp__row axp__row--two">
      <Card
        title="Continue Reading"
        sub="Pick up where you left off."
        action="View All"
        onAction={() => {
          onNavigate('/library');
        }}
      >
        <div className="axp__continue">
          {CONTINUE_READING.map((item) => (
            <article className="axp__continue-card" key={item.title}>
              <span className="axp__mini-cover" style={{ '--tone': item.tone } as CSSProperties}>
                {item.title}
              </span>
              <h4>{item.title}</h4>
              <p>{item.author}</p>
              <p className="axp__progress">
                <span
                  className="axp__progress-bar"
                  style={{ '--fill': `${String(item.percent)}%` } as CSSProperties}
                />
                <em>{String(item.percent)}%</em>
              </p>
              <p className="axp__continue-meta">{item.meta}</p>
              <button
                type="button"
                className="axp__continue-cta"
                onClick={() => {
                  onNavigate('/library');
                }}
              >
                Continue Reading
              </button>
            </article>
          ))}
        </div>
      </Card>
      <Card title="Reading Statistics" sub="Your reading journey in numbers." action="Last 30 days">
        <div className="axp__stat-grid">
          {READING_STAT_TILES.map((tile) => (
            <p className="axp__stat-tile" key={tile.label}>
              <IconChip icon={tile.icon} tint={tile.tint} ink={tile.ink} />
              <span>{tile.label}</span>
              <strong>{tile.value}</strong>
            </p>
          ))}
        </div>
        <div className="axp__goal">
          <span
            className="axp__donut"
            style={{ '--fill': `${String(PROFILE_PERSONA.goalPercent)}%` } as CSSProperties}
          >
            <em>{String(PROFILE_PERSONA.goalPercent)}%</em>
          </span>
          <p>
            Reading Goal
            <strong>{PROFILE_PERSONA.goalLabel}</strong>
          </p>
        </div>
      </Card>
    </div>
  );
}
